import Button from '@/src/components/atom/Button';
import Dialog from '@/src/components/atom/Dialog';
import Input from '@/src/components/atom/Input';
import RadioGroup from '@/src/components/atom/RadioGroup';
import FormContent from '@/src/components/molecule/FormContent';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { PartnershipCompainesDto } from '@/src/models/PartnershipCompainesDto';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { PutPartnershipCompaniesRequestBody } from '@/src/apis/rest/partnershipCompaines.dtos';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';
import { useMutation } from '@tanstack/react-query';
import { putPartnershipCompaniesAPI } from '@/src/apis/rest/partnershipCompanies';
import { toast } from '@/src/hooks/useToast';
import { TOAST_CONST } from '@/src/constants/toast';
import { areaCodeRegex, emailRegex, urlRegex } from '@/src/utils/regex';
import { usePrompt } from '@/src/hooks/usePrompt';

interface PartnershipCompaniesEditModalProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  onOk: () => void;
  data: PartnershipCompainesDto;
}

const formSchema = () => {
  return z.object({
    name: z.string().min(1),
    phone: z.string().regex(areaCodeRegex),
    email: z.string().regex(emailRegex).optional().nullable().or(z.literal('')),
    homepageUrl: z
      .string()
      .regex(urlRegex)
      .optional()
      .nullable()
      .or(z.literal('')),
    isDefault: z.boolean(),
    isUsing: z.boolean(),
  });
};

const PartnershipCompaniesEditModal = ({
  open: openProps,
  onOpenChange: onOpenChangeProps,
  onOk,
  data,
}: PartnershipCompaniesEditModalProps) => {
  const {
    Dialog: EditDialog,
    isOpen: isEditOpen,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const {
    Dialog: BlockAlertDialog,
    isOpen: isBlockAlertOpen,
    openDialog: openBlockAlertDialog,
    closeDialog: closeBlockAlertDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const [isHomepageUrlError, setIsHomepageUrlError] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  const form = useForm<PutPartnershipCompaniesRequestBody>({
    resolver: zodResolver(formSchema()),
    defaultValues: {
      ...data,
    },
    reValidateMode: 'onSubmit',
  });

  const putPartnershipCompaniesMutation = useMutation({
    mutationFn: putPartnershipCompaniesAPI,
  });

  const handleCloseModal = () => {
    if (form.formState.isDirty) {
      openBlockAlertDialog();
    } else {
      onOpenChangeProps(false);
    }
  };

  const handleSubmit = (values: PutPartnershipCompaniesRequestBody) => {
    putPartnershipCompaniesMutation.mutate(
      { ...values, id: data.id },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          onOpenChangeProps(false);
          onOk();
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      },
    );
  };

  const handleBlockModalOk = () => {
    onOpenChangeProps(false);
  };

  const renderFooter = () => {
    return (
      <div className="flex justify-end gap-2">
        <Button
          className="flex-1"
          buttonColor="outline"
          onClick={handleCloseModal}
        >
          {BUTTON_CONST.cancel}
        </Button>
        <Button
          key={String(form.formState.isValid)}
          type="button"
          className="flex-1"
          disabled={!form.formState.isDirty || !form.formState.isValid}
          onClick={openEditDialog}
          isLoading={putPartnershipCompaniesMutation.isPending}
        >
          {BUTTON_CONST.save}
        </Button>
      </div>
    );
  };

  usePrompt(form.formState.isDirty && !form.formState.isSubmitted);

  return data ? (
    <>
      <Dialog
        open={openProps}
        onOpenChange={handleCloseModal}
        contentProps={{
          style: {
            width: 'calc(100vw - 24px)',
          },
          className: 'max-w-[697px]',
        }}
        title="제휴사 정보 수정"
        footer={renderFooter()}
      >
        <Form {...form}>
          <form
            ref={formRef}
            className="flex flex-col gap-6"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="제휴사 명" isRequired error={error}>
                    <Input
                      containerClassName="flex-1"
                      onClear={() => field.onChange('')}
                      placeholder="제휴사 명을 입력해주세요"
                      {...field}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="대표 전화번호" isRequired error={error}>
                    <Input
                      containerClassName="flex-1"
                      onClear={() => field.onChange('')}
                      placeholder="대표 전화번호를 입력해주세요"
                      {...field}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="대표 이메일" error={error}>
                    <Input
                      containerClassName="flex-1"
                      onClear={() => field.onChange('')}
                      placeholder="대표 이메일을 입력해주세요"
                      {...field}
                    />
                  </FormContent>
                );
              }}
            />
            <FormField
              control={form.control}
              name="homepageUrl"
              render={({ field }) => {
                return (
                  <FormContent
                    label="홈페이지 링크"
                    error={
                      isHomepageUrlError
                        ? {
                            type: 'pattern',
                            message:
                              'url 형식을 확인해주세요 (https://example.com)',
                          }
                        : undefined
                    }
                  >
                    <Input
                      value={field?.value || undefined}
                      placeholder="제휴사의 홈페이지 링크를 입력해주세요"
                      onChange={e => {
                        field.onChange(e.target.value);

                        if (e.target.value) {
                          setIsHomepageUrlError(!urlRegex.test(e.target.value));
                        } else {
                          setIsHomepageUrlError(false);
                        }
                      }}
                      onClear={() => {
                        field.onChange('');
                        setIsHomepageUrlError(false);
                      }}
                    />
                  </FormContent>
                );
              }}
            />

            <FormField
              control={form.control}
              name="isUsing"
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent label="사용 여부" error={error}>
                    <RadioGroup
                      optionList={[
                        { label: '사용', value: 'true' },
                        { label: '미사용', value: 'false' },
                      ]}
                      rootProps={{
                        value: field.value ? 'true' : 'false',
                        onValueChange: (e: string) => {
                          field.onChange(e === 'true');
                        },
                      }}
                    />
                  </FormContent>
                );
              }}
            />
          </form>
        </Form>
        <BlockAlertDialog
          open={isBlockAlertOpen}
          onOpenChange={closeBlockAlertDialog}
          onCancel={() => {
            closeBlockAlertDialog();
          }}
          onOk={handleBlockModalOk}
          title="취소"
        >
          변경사항은 저장되지 않습니다. 취소하시겠습니까?
        </BlockAlertDialog>
      </Dialog>

      {isEditOpen && (
        <EditDialog
          open={isEditOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openEditDialog();
            } else {
              closeEditDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.edit}
          isLoading={putPartnershipCompaniesMutation.isPending}
        />
      )}
    </>
  ) : null;
};

export default PartnershipCompaniesEditModal;
